import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import QRCode from "react-qr-code";
import { Users, ScanLine, Maximize, Minimize } from "lucide-react";
import { ThemeProvider } from "../context/ThemeContext";
import Logo from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AURORA = {
  reshapers: ['rgba(103,74,157,0.5)', 'rgba(133,64,151,0.5)', 'rgba(162,64,138,0.4)', 'rgba(103,74,157,0.3)', 'rgba(133,64,151,0.4)'],
  teambuilding: ['rgba(255,185,2,0.5)', 'rgba(255,150,2,0.5)', 'rgba(255,116,3,0.4)', 'rgba(255,185,2,0.3)', 'rgba(255,150,2,0.4)'],
  marcom: ['rgba(79,180,204,0.5)', 'rgba(28,84,205,0.5)', 'rgba(42,112,171,0.4)', 'rgba(67,154,122,0.3)', 'rgba(79,180,204,0.4)'],
};

const AuroraBackground = ({ theme = 'reshapers' }) => {
  const c = AURORA[theme] || AURORA.reshapers;
  return (
    <div className="absolute inset-0 overflow-hidden bg-black">
      <div className="absolute inset-0 opacity-60" style={{
        background: `radial-gradient(ellipse 80% 50% at 20% 40%, ${c[0]} 0%, transparent 50%), radial-gradient(ellipse 60% 40% at 70% 60%, ${c[1]} 0%, transparent 50%), radial-gradient(ellipse 70% 50% at 50% 30%, ${c[2]} 0%, transparent 50%), radial-gradient(ellipse 50% 60% at 80% 20%, ${c[3]} 0%, transparent 50%), radial-gradient(ellipse 60% 40% at 30% 70%, ${c[4]} 0%, transparent 50%)`,
        animation: 'aurora 15s ease-in-out infinite',
      }} />
      <div className="absolute inset-0 opacity-40" style={{
        background: `radial-gradient(ellipse 70% 60% at 60% 50%, ${c[3]} 0%, transparent 50%), radial-gradient(ellipse 50% 40% at 30% 30%, ${c[4]} 0%, transparent 50%), radial-gradient(ellipse 80% 50% at 80% 70%, ${c[0]} 0%, transparent 50%)`,
        animation: 'aurora 12s ease-in-out infinite reverse',
      }} />
      <style>{`@keyframes aurora { 0%,100% { transform: translate(0,0) scale(1);} 25% { transform: translate(5%,-5%) scale(1.05);} 50% { transform: translate(-3%,3%) scale(0.95);} 75% { transform: translate(-5%,-3%) scale(1.02);} }`}</style>
    </div>
  );
};

const FullscreenButton = () => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  const toggle = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
    else document.exitFullscreen?.();
  };
  return (
    <button
      onClick={toggle}
      data-testid="welcome-fullscreen-btn"
      className="absolute top-5 right-5 z-20 p-3 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors"
      title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
    >
      {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
    </button>
  );
};

const WelcomeScreen = () => {
  const { sessionId } = useParams();
  const [session, setSession] = useState(null);
  const [participantCount, setParticipantCount] = useState(0);
  const [notFound, setNotFound] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [sRes, cRes] = await Promise.all([
        axios.get(`${API}/sessions/${sessionId}`),
        axios.get(`${API}/sessions/${sessionId}/participants/count`).catch(() => null),
      ]);
      setSession(sRes.data);
      if (cRes?.data?.total != null) setParticipantCount(cRes.data.total);
    } catch (error) {
      if (error?.response?.status === 404) setNotFound(true);
    }
  }, [sessionId]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 4000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (notFound) {
    return (
      <div className="h-screen w-screen bg-black flex items-center justify-center text-center p-6">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Session not found</h1>
          <p className="text-[#E2D9F3]">Check the link and try again.</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="h-screen w-screen bg-black flex items-center justify-center">
        <div className="w-12 h-12 rounded-full border-4 border-white/20 border-t-[#E91E8C] animate-spin" />
      </div>
    );
  }

  const joinUrl = `${window.location.origin}/join/${session.join_code}`;

  return (
    <ThemeProvider theme={session.theme}>
      <div
        className="h-screen w-screen bg-black flex flex-col items-center justify-center p-4 relative overflow-hidden"
        data-testid="welcome-screen"
      >
        <FullscreenButton />
        <AuroraBackground theme={session.theme} />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center relative z-10 w-full max-w-5xl"
        >
          <div className="mx-auto mb-4">
            <Logo size="xl" className="mx-auto" />
          </div>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold gradient-text mb-2" data-testid="welcome-session-name">
            {session.name}
          </h1>
          <p className="text-xl md:text-2xl lg:text-3xl text-[#E2D9F3] mb-8 flex items-center justify-center gap-3">
            <ScanLine className="w-7 h-7 md:w-9 md:h-9 text-[#E91E8C]" />
            Welcome — scan to join
          </p>

          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.25 }}
              className="glass-heavy rounded-3xl p-6 md:p-8"
            >
              <p className="text-lg md:text-2xl text-[#E2D9F3] mb-4">Scan to Join</p>
              <div className="bg-white p-4 md:p-6 rounded-2xl">
                <QRCode value={joinUrl} size={260} data-testid="welcome-qr" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.25 }}
              className="glass-heavy rounded-3xl p-6 md:p-8"
            >
              <p className="text-lg md:text-2xl text-[#E2D9F3] mb-2">Or enter code</p>
              <p className="text-5xl md:text-7xl lg:text-8xl font-mono font-bold text-[#CCFF00]" data-testid="welcome-join-code">
                {session.join_code}
              </p>
              <p className="text-sm md:text-base text-[#6B5E7C] mt-4 max-w-xs mx-auto break-words">
                Open your phone camera and point it at the code, or go to{" "}
                <span className="text-[#E2D9F3] font-mono break-all">{window.location.host}/join</span>
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
            className="mt-8 md:mt-10 flex items-center justify-center gap-8"
          >
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 md:w-10 md:h-10 text-[#E91E8C]" />
              <span className="text-xl md:text-3xl text-white">{session.groups?.length || 0} Teams</span>
            </div>
            <div className="flex items-center gap-3" data-testid="welcome-joined-count">
              <span className="text-3xl md:text-5xl font-bold gradient-text">{participantCount}</span>
              <span className="text-xl md:text-2xl text-[#E2D9F3]">joined</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </ThemeProvider>
  );
};

export default WelcomeScreen;
